'use client';
import * as React from 'react';
import { styled } from '@mui/material/styles';
import {
  getDataGridUtilityClass,
  gridColumnLookupSelector,
  gridExpandedSortedRowIdsSelector,
  gridFocusCellSelector,
  gridRowSelector,
  isAutogeneratedRow,
  useGridEvent,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import type { GridColDef, GridEventListener, GridRowId } from '@mui/x-data-grid-pro';
import { vars } from '@mui/x-data-grid-pro/internals';
import composeClasses from '@mui/utils/composeClasses';
import useId from '@mui/utils/useId';
import { useGridPrivateApiContext } from '../../hooks/utils/useGridPrivateApiContext';
import { useGridRootProps } from '../../hooks/utils/useGridRootProps';
import type { DataGridPremiumProcessedProps } from '../../models/dataGridPremiumProps';
import type {
  GridComputedColumnDefinition,
  GridComputedColumnType,
  GridComputedColumnValidationResult,
} from '../../hooks/features/computedColumns/gridComputedColumnsInterfaces';
import {
  gridComputedColumnsRevisionSelector,
  gridComputedColumnsSelector,
} from '../../hooks/features/computedColumns/gridComputedColumnsSelectors';
import { deriveComputedColumnField } from '../../hooks/features/computedColumns/deriveComputedColumnField';
import { isComputedColumnEditable } from '../../hooks/features/computedColumns/gridComputedColumnsUtils';
import { getFormulaExpression, isFormulaSource } from '../../hooks/features/formula/engine';
import type { FormulaSourceSpan } from '../../hooks/features/formula/engine';
import { previewFormulaResult } from '../../hooks/features/formula/gridFormulaPreview';
import { gridFormulaActiveEditSelector } from '../../hooks/features/formula/gridFormulaSelectors';
import { coerceComputedResult } from '../../hooks/features/formula/gridComputedColumnsRuntime';
import {
  applyComputedColDefOverrides,
  createComputedBaseColDef,
  resolveComputedColDefOverrides,
} from '../../hooks/features/formula/createComputedColDef';
import type {
  GridFormulaActiveEdit,
  GridFormulaResult,
} from '../../hooks/features/formula/gridFormulaInterfaces';
import { GridFormulaEditable, isComposingKeyEvent } from '../GridFormulaEditable';
import type { GridFormulaEditableHandle } from '../GridFormulaEditable';
import { GridComputedColumnsPanelReferencePane } from './GridComputedColumnsPanelReferencePane';

/**
 * The field of a draft that has none yet (a new column before it has a name):
 * the reference model and the preview need an owner cell.
 */
const DRAFT_FIELD_PLACEHOLDER = '__computed_draft__';
const PREVIEW_DEBOUNCE_MS = 150;
const EMPTY_SPANS: FormulaSourceSpan[] = [];
const CURRENCY_CODE_REGEX = /^[A-Za-z]{3}$/;
const MAX_FRACTION_DIGITS = 20;

const COLUMN_TYPES: GridComputedColumnType[] = ['number', 'string', 'boolean', 'date', 'dateTime'];
const COLUMN_TYPE_LABELS: Record<
  GridComputedColumnType,
  | 'computedColumnTypeNumber'
  | 'computedColumnTypeString'
  | 'computedColumnTypeBoolean'
  | 'computedColumnTypeDate'
  | 'computedColumnTypeDateTime'
> = {
  number: 'computedColumnTypeNumber',
  string: 'computedColumnTypeString',
  boolean: 'computedColumnTypeBoolean',
  date: 'computedColumnTypeDate',
  dateTime: 'computedColumnTypeDateTime',
};

type NumberFormatStyle = 'decimal' | 'percent' | 'currency';
const FORMAT_STYLES: NumberFormatStyle[] = ['decimal', 'percent', 'currency'];
const FORMAT_STYLE_LABELS: Record<
  NumberFormatStyle,
  | 'computedColumnFormatStyleDecimal'
  | 'computedColumnFormatStylePercent'
  | 'computedColumnFormatStyleCurrency'
> = {
  decimal: 'computedColumnFormatStyleDecimal',
  percent: 'computedColumnFormatStylePercent',
  currency: 'computedColumnFormatStyleCurrency',
};

export interface GridComputedColumnsPanelEditorSession {
  /**
   * The stored definition being edited, `null` for a new column.
   */
  definition: GridComputedColumnDefinition | null;
  sampleRowId?: GridRowId;
  /**
   * The index the new column is inserted at (the column menu inserts after its column).
   */
  columnIndex?: number;
}

export type GridComputedColumnsPanelEditorOutcome =
  { type: 'applied'; field: string } | { type: 'deleted' } | { type: 'cancelled' };

export interface GridComputedColumnsPanelEditorProps {
  session: GridComputedColumnsPanelEditorSession;
  /**
   * Called once the editor is done (applied, deleted or cancelled); the host
   * decides where to go next.
   * @param {GridComputedColumnsPanelEditorOutcome} outcome What the editor did.
   */
  onDone: (outcome: GridComputedColumnsPanelEditorOutcome) => void;
}

interface EditorDraft {
  headerName: string;
  field: string;
  formula: string;
  type: GridComputedColumnType;
  formatStyle: NumberFormatStyle;
  currency: string;
  /**
   * Number of fraction digits as typed; empty for the default of the type.
   */
  decimals: string;
  grouping: boolean;
}

type OwnerState = DataGridPremiumProcessedProps;

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['computedColumnsPanelEditor'],
    field: ['computedColumnsPanelField'],
    formula: ['computedColumnsPanelFormula'],
    preview: ['computedColumnsPanelPreview'],
    validation: ['computedColumnsPanelValidation'],
    footer: ['computedColumnsPanelFooter'],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const GridComputedColumnsPanelEditorRoot = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelEditor',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  font: vars.typography.font.body,
});

const GridComputedColumnsPanelEditorBody = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelEditorBody',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing(2),
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: vars.spacing(2, 1.5),
  boxSizing: 'border-box',
});

const GridComputedColumnsPanelFieldRoot = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelField',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing(1),
  minWidth: 0,
});

const GridComputedColumnsPanelFieldRow = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelFieldRow',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  gap: vars.spacing(1),
  minWidth: 0,
  '& > *': {
    flex: 1,
    minWidth: 0,
  },
});

const GridComputedColumnsPanelLabel = styled('label', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelLabel',
})<{ ownerState: OwnerState }>({
  font: vars.typography.font.small,
  color: vars.colors.foreground.muted,
});

// The formula surface: an outlined box the editable fills. The editable keeps
// its text on one line (newlines are normalized away), so wrapping is what
// makes a long formula readable in a narrow panel.
const GridComputedColumnsPanelFormula = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelFormula',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  minHeight: 72,
  padding: vars.spacing(1, 0),
  border: `1px solid ${vars.colors.border.base}`,
  borderRadius: vars.radius.base,
  background: vars.colors.background.base,
  boxSizing: 'border-box',
  cursor: 'text',
  '&:focus-within': {
    borderColor: vars.colors.interactive.selected,
  },
  '& [contenteditable]': {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    overflowX: 'visible',
  },
});

const GridComputedColumnsPanelPreview = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelPreview',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing(0.5),
  padding: vars.spacing(1, 1.5),
  border: `1px solid ${vars.colors.border.base}`,
  borderRadius: vars.radius.base,
  background: vars.colors.background.overlay,
  minWidth: 0,
});

const GridComputedColumnsPanelPreviewRow = styled('span', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelPreviewRow',
})<{ ownerState: OwnerState }>({
  font: vars.typography.font.small,
  color: vars.colors.foreground.muted,
});

const GridComputedColumnsPanelPreviewValue = styled('span', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelPreviewValue',
})<{ ownerState: OwnerState }>({
  minHeight: '1.5em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&[data-error="true"]': {
    color: vars.colors.foreground.error,
  },
});

const GridComputedColumnsPanelReadOnlyHint = styled('p', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelReadOnlyHint',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing(1),
  margin: 0,
  font: vars.typography.font.small,
  color: vars.colors.foreground.muted,
});

const GridComputedColumnsPanelValidation = styled('ul', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelValidation',
})<{ ownerState: OwnerState }>({
  margin: 0,
  padding: vars.spacing(0, 0, 0, 2),
  font: vars.typography.font.small,
  color: vars.colors.foreground.error,
});

const GridComputedColumnsPanelFooter = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanelFooter',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing(1),
  padding: vars.spacing(1.5),
  borderTop: `1px solid ${vars.colors.border.base}`,
  '& > *': {
    whiteSpace: 'nowrap',
  },
  '& > :last-child': {
    marginLeft: 'auto',
  },
});

const NOOP_GETTERS = {
  valueGetter: () => undefined,
  groupingValueGetter: () => undefined,
  rowSpanValueGetter: () => undefined,
};

// The preview shows an error result before formatting: the formatter only receives values.
const IS_NEVER_ERROR_ROW = () => false;

type EditorFormatDraft = Pick<EditorDraft, 'formatStyle' | 'currency' | 'decimals' | 'grouping'>;

/**
 * What the format controls show for a stored `numberFormat`. The controls are
 * coarser than the options (one Decimals field for both fraction bounds), so
 * the seed is also what `buildNumberFormat` compares the draft to: a control
 * still equal to its seed was not touched.
 */
function createFormatSeed(numberFormat: Intl.NumberFormatOptions | undefined): EditorFormatDraft {
  const style = numberFormat?.style;
  return {
    formatStyle: style === 'percent' || style === 'currency' ? style : 'decimal',
    currency: numberFormat?.currency ?? 'USD',
    decimals:
      numberFormat?.maximumFractionDigits === undefined
        ? ''
        : String(numberFormat.maximumFractionDigits),
    grouping: numberFormat?.useGrouping !== false,
  };
}

function createDraft(definition: GridComputedColumnDefinition | null): EditorDraft {
  return {
    headerName: definition?.headerName ?? '',
    field: definition?.field ?? '',
    // A new formula starts with the `=`, so the autocomplete opens right away.
    formula: definition?.formula ?? '=',
    type: definition?.type ?? 'number',
    ...createFormatSeed(definition?.numberFormat),
  };
}

function normalizeFormula(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '' || isFormulaSource(trimmed)) {
    return trimmed;
  }
  return `=${trimmed}`;
}

function parseDecimals(decimals: string): number | null | undefined {
  if (decimals.trim() === '') {
    return undefined;
  }
  const value = Number(decimals);
  if (!Number.isInteger(value) || value < 0 || value > MAX_FRACTION_DIGITS) {
    return null;
  }
  return value;
}

/**
 * The `numberFormat` of the draft. The editor manages three groups of options
 * (style + currency, the fraction bounds, the grouping): a group the user
 * changed replaces the stored options of that group, an untouched one keeps
 * them as stored (a stored `{ minimumFractionDigits: 0, maximumFractionDigits: 2 }`
 * is not what the single Decimals field would write). Anything else in a
 * stored format is kept.
 */
function buildNumberFormat(
  draft: EditorDraft,
  stored: Intl.NumberFormatOptions | undefined,
): Intl.NumberFormatOptions | undefined {
  if (draft.type !== 'number') {
    return undefined;
  }
  const seed = createFormatSeed(stored);
  const options: Intl.NumberFormatOptions = { ...stored };
  if (draft.formatStyle !== seed.formatStyle || draft.currency !== seed.currency) {
    delete options.style;
    delete options.currency;
    if (draft.formatStyle !== 'decimal') {
      options.style = draft.formatStyle;
    }
    if (draft.formatStyle === 'currency') {
      options.currency = draft.currency.toUpperCase();
    }
  }
  if (draft.decimals !== seed.decimals) {
    delete options.minimumFractionDigits;
    delete options.maximumFractionDigits;
    const decimals = parseDecimals(draft.decimals);
    if (typeof decimals === 'number') {
      options.minimumFractionDigits = decimals;
      options.maximumFractionDigits = decimals;
    }
  }
  if (draft.grouping !== seed.grouping) {
    delete options.useGrouping;
    if (!draft.grouping) {
      options.useGrouping = false;
    }
  }
  return Object.keys(options).length === 0 ? undefined : options;
}

/**
 * The stored shape of a draft: optional properties are left out when they have no value.
 */
function buildDefinition(
  draft: EditorDraft,
  stored: GridComputedColumnDefinition | null,
): GridComputedColumnDefinition {
  const definition: GridComputedColumnDefinition = {
    field: draft.field.trim(),
    headerName: draft.headerName.trim(),
    formula: normalizeFormula(draft.formula),
    type: draft.type,
  };
  const numberFormat = buildNumberFormat(draft, stored?.numberFormat);
  if (numberFormat !== undefined) {
    definition.numberFormat = numberFormat;
  }
  if (stored?.description !== undefined) {
    definition.description = stored.description;
  }
  return definition;
}

function isFormatInvalid(draft: EditorDraft): { currency: boolean; decimals: boolean } {
  const isNumber = draft.type === 'number';
  return {
    currency:
      isNumber && draft.formatStyle === 'currency' && !CURRENCY_CODE_REGEX.test(draft.currency),
    decimals: isNumber && parseDecimals(draft.decimals) === null,
  };
}

/**
 * Whether the formula `activeEdit` is the editor's own (a draft for its field —
 * a cell editor carries no draft, the formula bar's draft targets a data cell).
 */
function ownsActiveEdit(edit: GridFormulaActiveEdit | null, field: string | null): boolean {
  return edit !== null && edit.draft !== undefined && field !== null && edit.field === field;
}

function inferColumnType(value: unknown): GridComputedColumnType | null {
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (value instanceof Date) {
    const hasTime =
      value.getHours() !== 0 ||
      value.getMinutes() !== 0 ||
      value.getSeconds() !== 0 ||
      value.getMilliseconds() !== 0;
    return hasTime ? 'dateTime' : 'date';
  }
  return null;
}

/**
 * The editor view of the computed columns panel: a local draft of one
 * definition (name, field, formula, result type and number format), validated
 * on every change, with a debounced preview of the formula on one row. The
 * draft only reaches the model on Apply; unmounting discards it.
 * A definition with `editable: false` opens read-only: the controls show it, and
 * nothing can be applied or deleted.
 */
function GridComputedColumnsPanelEditor(props: GridComputedColumnsPanelEditorProps) {
  const { session, onDone } = props;
  const apiRef = useGridPrivateApiContext();
  const rootProps = useGridRootProps();
  const classes = useUtilityClasses(rootProps);
  const id = useId();
  const editableRef = React.useRef<GridFormulaEditableHandle | null>(null);
  const nameInputRef = React.useRef<HTMLInputElement | null>(null);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const cancelButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // The definition the draft starts from: the one the session opened, until the column
  // is locked or unlocked under the editor (see the effect below).
  const [storedDefinition, setStoredDefinition] = React.useState(session.definition);
  const isEditing = storedDefinition !== null;
  const a1Notation = Boolean(rootProps.formulaA1Notation);

  const [draft, setDraft] = React.useState<EditorDraft>(() => createDraft(storedDefinition));
  // The derived field stops following the name once the user edits it.
  const [fieldTouched, setFieldTouched] = React.useState(isEditing);
  // The result type stops following the preview once the user picks one.
  const [typeTouched, setTypeTouched] = React.useState(isEditing);
  const [dirty, setDirty] = React.useState(false);

  // ----- Read-only (`editable: false`) -----

  // The lock is read from the model: a definition locked or unlocked while the editor is
  // open (from code or by the parent) switches the editor right away.
  const model = useGridSelector(apiRef, gridComputedColumnsSelector);
  const liveDefinition = isEditing
    ? (model.find((definition) => definition.field === storedDefinition.field) ?? storedDefinition)
    : null;
  const readOnly = liveDefinition !== null && !isComputedColumnEditable(liveDefinition);

  // Whether the focus is in the editor, read during the render that applies a lock: by the
  // time the effect below runs, the browser has moved the focus away from a control the lock
  // removed (Apply, the Insert pane) or made unfocusable (the formula).
  const root = rootRef.current;
  const focusInside = root !== null && root.contains(root.ownerDocument.activeElement);

  // A read-only editor shows the definition as it is now, and a lock or an unlock starts
  // over from it: the draft of a column that became locked is dropped. A change of an
  // editable definition keeps the draft, as before.
  React.useEffect(() => {
    if (
      liveDefinition === null ||
      liveDefinition === storedDefinition ||
      (!readOnly && isComputedColumnEditable(storedDefinition!))
    ) {
      return;
    }
    setStoredDefinition(liveDefinition);
    setDraft(createDraft(liveDefinition));
    setDirty(false);
    // Only a lock moves the focus, and only when the editor had it: the focus is left
    // alone when a locked definition changes or the user is elsewhere.
    if (readOnly && isComputedColumnEditable(storedDefinition!) && focusInside) {
      cancelButtonRef.current?.focus({ preventScroll: true });
    }
  }, [focusInside, liveDefinition, readOnly, storedDefinition]);

  const updateDraft = React.useCallback((changes: Partial<EditorDraft>) => {
    setDraft((prev) => ({ ...prev, ...changes }));
    setDirty(true);
  }, []);

  // ----- Preview row (follows the focused cell) -----

  const isDataRow = React.useCallback(
    (rowId: GridRowId) => {
      const row = apiRef.current.getRow(rowId);
      return row != null && !isAutogeneratedRow(row);
    },
    [apiRef],
  );

  const focusedCell = useGridSelector(apiRef, gridFocusCellSelector);
  const expandedSortedRowIds = useGridSelector(apiRef, gridExpandedSortedRowIdsSelector);

  const [previewRowId, setPreviewRowId] = React.useState<GridRowId | null>(() => {
    if (session.sampleRowId !== undefined && isDataRow(session.sampleRowId)) {
      return session.sampleRowId;
    }
    if (focusedCell !== null && isDataRow(focusedCell.id)) {
      return focusedCell.id;
    }
    return expandedSortedRowIds.find(isDataRow) ?? null;
  });

  // Only a focus MOVE after the editor opened changes the preview row (the
  // request's sample row wins over a cell focused earlier). Focus leaving the
  // cells keeps the last row.
  const lastFocusedCellRef = React.useRef(focusedCell);
  React.useEffect(() => {
    if (focusedCell === lastFocusedCellRef.current) {
      return;
    }
    lastFocusedCellRef.current = focusedCell;
    if (focusedCell !== null && isDataRow(focusedCell.id)) {
      setPreviewRowId(focusedCell.id);
    }
  }, [focusedCell, isDataRow]);

  // A preview row removed from the grid falls back to the first data row; so
  // does an editor opened on an empty grid once rows arrive (`null` → `null`
  // is a no-op, the effect cannot loop).
  React.useEffect(() => {
    if (previewRowId === null || apiRef.current.getRow(previewRowId) == null) {
      setPreviewRowId(expandedSortedRowIds.find(isDataRow) ?? null);
    }
  }, [apiRef, expandedSortedRowIds, previewRowId, isDataRow]);

  const previewRowIndex = React.useMemo(() => {
    if (previewRowId === null) {
      return null;
    }
    const index = expandedSortedRowIds.indexOf(previewRowId);
    return index === -1 ? null : index + 1;
  }, [expandedSortedRowIds, previewRowId]);

  // ----- Draft definition + validation -----

  const draftDefinition = React.useMemo<GridComputedColumnDefinition>(
    () => buildDefinition(draft, storedDefinition),
    [draft, storedDefinition],
  );

  // The verdict is read from the columns state (fields taken or freed, referenced columns
  // removed): it follows every hydration, so an undo/redo under the editor shows up without
  // a keystroke.
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
  const validation = React.useMemo<GridComputedColumnValidationResult>(
    () =>
      apiRef.current.validateComputedColumnDefinition!(
        draftDefinition,
        isEditing ? { ignoreField: storedDefinition.field } : undefined,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `columnLookup` is read through the state
    [apiRef, columnLookup, draftDefinition, isEditing, storedDefinition],
  );

  // The shape rules guard the fields the editor creates. The field of a stored column is
  // locked (`updateComputedColumn` cannot change it), so a shape issue of a field brought
  // from code (`unit-price`, an A1-like `q1`) is reported but does not block an edit of the
  // formula — the user would have no way out but Delete.
  const hasBlockingIssue = React.useCallback(
    (result: GridComputedColumnValidationResult, definition: GridComputedColumnDefinition) =>
      result.issues.some(
        (issue) =>
          !(
            isEditing &&
            definition.field === storedDefinition.field &&
            (issue.code === 'fieldInvalid' || issue.code === 'fieldA1Like')
          ),
      ),
    [isEditing, storedDefinition],
  );

  const { currency: currencyInvalid, decimals: decimalsInvalid } = isFormatInvalid(draft);
  const canApply =
    !hasBlockingIssue(validation, draftDefinition) && !currencyInvalid && !decimalsInvalid;
  const showValidation = (dirty || isEditing) && validation.issues.length > 0;

  // ----- Preview (debounced, through the cell path) -----

  const [rawPreview, setRawPreview] = React.useState<GridFormulaResult | null>(null);
  const expression = getFormulaExpression(draftDefinition.formula).trim();
  const ownerField = draftDefinition.field === '' ? DRAFT_FIELD_PLACEHOLDER : draftDefinition.field;

  const computePreview = React.useCallback(
    (rowId: GridRowId) =>
      previewFormulaResult(apiRef, { id: rowId, field: ownerField }, `=${expression}`, {
        a1Notation,
      }),
    [apiRef, expression, ownerField, a1Notation],
  );

  // What the result depends on besides the draft, none of which a render of the
  // editor tells by itself:
  // - the data of the preview row (`updateRows`, a replaced `rows` prop) — the
  //   row-scoped selector ignores the updates of the other rows;
  // - the revision: the getters of the referenced columns (computed or not) and
  //   the function registry;
  // - a formula cell of the preview row re-evaluated: the row object is not
  //   touched, and the revision only follows the fields the STORED computed
  //   columns reference.
  const previewRow = useGridSelector(apiRef, gridRowSelector, previewRowId as GridRowId);
  const revision = useGridSelector(apiRef, gridComputedColumnsRevisionSelector);
  const [evaluationTick, setEvaluationTick] = React.useState(0);
  const handleFormulaEvaluated: GridEventListener<'formulaEvaluated'> = ({ changedCells }) => {
    if (previewRowId !== null && changedCells.some((cell) => cell.id === previewRowId)) {
      setEvaluationTick((prev) => prev + 1);
    }
  };
  useGridEvent(apiRef, 'formulaEvaluated', handleFormulaEvaluated);

  React.useEffect(() => {
    if (previewRowId === null || expression === '') {
      setRawPreview(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      setRawPreview(computePreview(previewRowId));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [previewRowId, expression, computePreview, previewRow, revision, evaluationTick]);

  React.useEffect(() => {
    if (typeTouched || rawPreview === null || rawPreview.type === 'error') {
      return;
    }
    const inferred = inferColumnType(rawPreview.value);
    if (inferred !== null) {
      setDraft((prev) => (prev.type === inferred ? prev : { ...prev, type: inferred }));
    }
  }, [rawPreview, typeTouched]);

  // The same formatter the cell will use (number format, date/boolean
  // formatting, the `computedColDef` formatter). A format still being typed (an
  // incomplete currency code) would make `Intl.NumberFormat` throw: it is left
  // out until it is valid.
  const previewFormatValid = !currencyInvalid && !decimalsInvalid;
  const { computedColDef } = rootProps;
  // Apply keeps the lock of the stored column, which a `computedColDef` function can read.
  const liveEditable = liveDefinition?.editable;
  const previewColDefs = React.useMemo<GridColDef[]>(() => {
    // The field of the draft, empty until a new column has a name: the placeholder
    // owner field of the formula preview never reaches `computedColDef`.
    const { numberFormat, ...definition } = draftDefinition;
    const previewDefinition: GridComputedColumnDefinition = {
      ...definition,
      ...(previewFormatValid && numberFormat !== undefined ? { numberFormat } : {}),
      ...(liveEditable !== undefined ? { editable: liveEditable } : {}),
    };
    const baseColDef = createComputedBaseColDef(previewDefinition, NOOP_GETTERS as never);
    // The overrides are resolved for the draft, so that a `computedColDef` function
    // follows the draft type. One that throws on a draft leaves the base formatting.
    let overrides: Partial<GridColDef> | null;
    try {
      overrides = resolveComputedColDefOverrides(previewDefinition, computedColDef);
    } catch {
      overrides = null;
    }
    const colDef = applyComputedColDefOverrides(
      baseColDef,
      previewDefinition,
      overrides,
      IS_NEVER_ERROR_ROW,
    );
    return colDef === baseColDef ? [baseColDef] : [colDef, baseColDef];
  }, [draftDefinition, previewFormatValid, liveEditable, computedColDef]);

  const preview = React.useMemo(() => {
    if (rawPreview === null || previewRowId === null) {
      return null;
    }
    const result = coerceComputedResult(rawPreview, draftDefinition.type);
    if (result.type === 'error') {
      return { text: result.code, error: true };
    }
    const row = apiRef.current.getRow(previewRowId) ?? {};
    // An empty result is formatted too, as in the cells. A `computedColDef` formatter
    // written for another type than the draft's falls back to the base formatting,
    // and that one to the value.
    let formatted: unknown = result.value;
    for (const colDef of previewColDefs) {
      try {
        formatted = colDef.valueFormatter
          ? colDef.valueFormatter(result.value as never, row, colDef, apiRef)
          : result.value;
        break;
      } catch {
        // The next formatter of the chain.
      }
    }
    // Like the cell, which renders the value when the formatter returns nothing.
    const shown = formatted ?? result.value;
    return { text: shown == null ? '' : String(shown), error: false };
  }, [apiRef, rawPreview, previewRowId, draftDefinition.type, previewColDefs]);

  // The parse error's span, shifted from the normalized source to the text the
  // editable displays (leading whitespace, a missing `=`); shown with the
  // validation list.
  const errorSpans = React.useMemo<FormulaSourceSpan[]>(() => {
    if (!showValidation) {
      return EMPTY_SPANS;
    }
    const trimmed = draft.formula.trim();
    const delta = draft.formula.indexOf(trimmed) - (isFormulaSource(trimmed) ? 0 : 1);
    const spans = validation.issues.flatMap((issue) => {
      if (issue.code !== 'parseError' || issue.span === undefined) {
        return [];
      }
      const start = issue.span.start + delta;
      const end = issue.span.end + delta;
      // An empty span (unexpected end of the formula) underlines the character before it.
      return [start < end ? { start, end } : { start: Math.max(0, start - 1), end: start }];
    });
    return spans.length === 0 ? EMPTY_SPANS : spans;
  }, [showValidation, validation.issues, draft.formula]);

  // ----- In-grid outlines of the referenced cells of the preview row -----

  // The editor publishes its draft as the formula `activeEdit` — the reference
  // overlay outlines the referenced cells of the preview row from it. It
  // yields to a cell editor or a formula bar draft (theirs carries another
  // field) and reclaims once they clear it (the bar clears any draft on every
  // cell focus move, `cellEditStop` clears unconditionally).
  const activeEdit = useGridSelector(apiRef, gridFormulaActiveEditSelector);
  // The field last published: the draft field follows the name of a new column,
  // so the editor's own entry can carry a previous field.
  const publishedFieldRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (previewRowId === null) {
      return;
    }
    if (
      activeEdit !== null &&
      !ownsActiveEdit(activeEdit, ownerField) &&
      !ownsActiveEdit(activeEdit, publishedFieldRef.current)
    ) {
      return;
    }
    publishedFieldRef.current = ownerField;
    apiRef.current.setFormulaActiveEdit!({
      id: previewRowId,
      field: ownerField,
      draft: draft.formula,
    });
  }, [apiRef, activeEdit, previewRowId, ownerField, draft.formula]);

  React.useEffect(() => {
    const api = apiRef.current;
    return () => {
      if (ownsActiveEdit(gridFormulaActiveEditSelector(apiRef), publishedFieldRef.current)) {
        api.setFormulaActiveEdit!(null);
      }
    };
  }, [apiRef]);

  // ----- Handlers -----

  const handleNameChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const headerName = event.target.value;
      if (fieldTouched) {
        updateDraft({ headerName });
        return;
      }
      const existingFields = new Set<string>(Object.keys(gridColumnLookupSelector(apiRef)));
      gridComputedColumnsSelector(apiRef).forEach((definition) =>
        existingFields.add(definition.field),
      );
      updateDraft({
        headerName,
        field:
          headerName.trim() === '' ? '' : deriveComputedColumnField(headerName, existingFields),
      });
    },
    [apiRef, fieldTouched, updateDraft],
  );

  const handleFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldTouched(true);
      updateDraft({ field: event.target.value });
    },
    [updateDraft],
  );

  const handleFormulaChange = React.useCallback(
    (text: string) => {
      updateDraft({ formula: text });
    },
    [updateDraft],
  );

  const handleTypeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setTypeTouched(true);
      updateDraft({ type: event.target.value as GridComputedColumnType });
    },
    [updateDraft],
  );

  const handleApply = React.useCallback(() => {
    if (!canApply || readOnly) {
      return;
    }
    let definition = draftDefinition;
    // The type follows the debounced preview: an Apply faster than the debounce
    // resolves it from the formula as it is now, not as it was 150 ms ago.
    if (!typeTouched && previewRowId !== null && expression !== '') {
      const result = computePreview(previewRowId);
      const inferred =
        result === null || result.type === 'error' ? null : inferColumnType(result.value);
      if (inferred !== null && inferred !== draft.type) {
        const finalDraft = { ...draft, type: inferred };
        definition = buildDefinition(finalDraft, storedDefinition);
        const formatInvalid = isFormatInvalid(finalDraft);
        if (
          formatInvalid.currency ||
          formatInvalid.decimals ||
          hasBlockingIssue(
            apiRef.current.validateComputedColumnDefinition!(
              definition,
              isEditing ? { ignoreField: storedDefinition.field } : undefined,
            ),
            definition,
          )
        ) {
          // The new type brings back format fields that were hidden (and not
          // checked): show them instead of saving what the user cannot see.
          setDraft(finalDraft);
          return;
        }
      }
    }
    const { field } = definition;
    if (isEditing) {
      apiRef.current.updateComputedColumn(
        storedDefinition.field,
        {
          // `updateComputedColumn` merges: a format removed by the editor is cleared explicitly.
          numberFormat: undefined,
          ...definition,
        },
        { undoable: true },
      );
    } else {
      apiRef.current.addComputedColumn(definition, {
        columnIndex: session.columnIndex,
        undoable: true,
      });
    }
    onDone({ type: 'applied', field });
  }, [
    apiRef,
    canApply,
    computePreview,
    draft,
    draftDefinition,
    expression,
    hasBlockingIssue,
    isEditing,
    onDone,
    previewRowId,
    readOnly,
    session.columnIndex,
    storedDefinition,
    typeTouched,
  ]);

  const handleDelete = React.useCallback(() => {
    if (readOnly) {
      return;
    }
    if (isEditing) {
      apiRef.current.removeComputedColumn(storedDefinition.field, { undoable: true });
    }
    onDone({ type: 'deleted' });
  }, [apiRef, isEditing, onDone, readOnly, storedDefinition]);

  const handleCancel = React.useCallback(() => {
    onDone({ type: 'cancelled' });
  }, [onDone]);

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // An Enter that confirms an IME composition belongs to the IME.
      if (isComposingKeyEvent(event)) {
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApply();
      }
    },
    [handleApply],
  );

  // Ctrl/Cmd+Enter applies from any field. Captured before the field sees it:
  // a select would open its menu on Enter, the editable would accept a
  // suggestion.
  const handleRootKeyDownCapture = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isComposingKeyEvent(event)) {
        return;
      }
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
        handleApply();
      }
    },
    [handleApply],
  );

  // Escape cancels unless a field consumed it: the editable's popup and the
  // select menus stop their own Escape, the reference search clears itself first.
  const handleRootKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // An Escape that cancels an IME composition does not cancel the editor.
      if (isComposingKeyEvent(event)) {
        return;
      }
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        handleCancel();
        return;
      }
      // Home/End with the caret already at that edge of the formula: the
      // browser would otherwise scroll the panel body instead (and away from
      // the formula).
      if ((event.key === 'End' || event.key === 'Home') && !event.defaultPrevented) {
        const editable = editableRef.current;
        const root = editable?.getRoot();
        if (root && root.contains(event.target as Node)) {
          const caret = editable!.getCaret();
          const edge = event.key === 'End' ? (root.textContent ?? '').length : 0;
          if (caret === edge) {
            event.preventDefault();
          }
        }
      }
    },
    [handleCancel],
  );

  const handleInsert = React.useCallback(
    (text: string, event: React.SyntheticEvent) => {
      const editable = editableRef.current;
      if (!editable) {
        return;
      }
      // A cleared formula gets its `=` back with the first inserted token.
      editable.insertText(draft.formula.trim() === '' ? `=${text}` : text, event);
      editable.focus();
    },
    [draft.formula],
  );

  // Clicking the formula surface around the editable focuses it (like an input's padding).
  const handleFormulaSurfaceMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // A read-only formula is selected like any text.
      if (readOnly) {
        return;
      }
      const root = editableRef.current?.getRoot();
      if (root && !root.contains(event.target as Node)) {
        event.preventDefault();
        editableRef.current?.focus();
        editableRef.current?.placeCaret(null);
      }
    },
    [readOnly],
  );

  // Focus on open: the name of a new column, the formula of an existing one, the name of a
  // read-only one (the read-only formula cannot take the focus).
  const openedReadOnlyRef = React.useRef(readOnly);
  React.useEffect(() => {
    if (isEditing && !openedReadOnlyRef.current) {
      editableRef.current?.focus();
      editableRef.current?.placeCaret(null);
    } else {
      nameInputRef.current?.focus();
    }
  }, [isEditing]);

  const getLocaleText = apiRef.current.getLocaleText;
  const nameId = `${id}-name`;
  const fieldId = `${id}-field`;
  const formulaLabelId = `${id}-formula-label`;
  const typeId = `${id}-type`;
  const typeLabelId = `${id}-type-label`;
  const styleId = `${id}-style`;
  const styleLabelId = `${id}-style-label`;
  const previewLabelId = `${id}-preview-label`;
  const readOnlyHintId = `${id}-read-only-hint`;

  return (
    <GridComputedColumnsPanelEditorRoot
      ref={rootRef}
      ownerState={rootProps}
      className={classes.root}
      onKeyDown={handleRootKeyDown}
      onKeyDownCapture={handleRootKeyDownCapture}
    >
      <GridComputedColumnsPanelEditorBody ownerState={rootProps}>
        {readOnly && (
          <GridComputedColumnsPanelReadOnlyHint ownerState={rootProps} id={readOnlyHintId}>
            <rootProps.slots.computedColumnReadOnlyIcon fontSize="small" />
            {getLocaleText('computedColumnReadOnlyHint')}
          </GridComputedColumnsPanelReadOnlyHint>
        )}
        <GridComputedColumnsPanelFieldRoot ownerState={rootProps} className={classes.field}>
          <rootProps.slots.baseTextField
            id={nameId}
            inputRef={nameInputRef}
            label={getLocaleText('computedColumnNameLabel')}
            value={draft.headerName}
            onChange={handleNameChange}
            size="small"
            fullWidth
            slotProps={{
              htmlInput: {
                onKeyDown: handleInputKeyDown,
                readOnly,
                'aria-describedby': readOnly ? readOnlyHintId : undefined,
              },
            }}
            {...rootProps.slotProps?.baseTextField}
          />
        </GridComputedColumnsPanelFieldRoot>
        <GridComputedColumnsPanelFieldRoot ownerState={rootProps} className={classes.field}>
          <rootProps.slots.baseTextField
            id={fieldId}
            label={getLocaleText('computedColumnFieldLabel')}
            value={draft.field}
            onChange={handleFieldChange}
            disabled={isEditing}
            size="small"
            fullWidth
            slotProps={{
              htmlInput: { onKeyDown: handleInputKeyDown, readOnly: isEditing },
            }}
            {...rootProps.slotProps?.baseTextField}
          />
        </GridComputedColumnsPanelFieldRoot>
        <GridComputedColumnsPanelFieldRoot ownerState={rootProps} className={classes.field}>
          <GridComputedColumnsPanelLabel ownerState={rootProps} id={formulaLabelId}>
            {getLocaleText('computedColumnFormulaLabel')}
          </GridComputedColumnsPanelLabel>
          <GridComputedColumnsPanelFormula
            ownerState={rootProps}
            className={classes.formula}
            onMouseDown={handleFormulaSurfaceMouseDown}
          >
            <GridFormulaEditable
              ref={editableRef}
              value={draft.formula}
              ownerCell={previewRowId === null ? null : { id: previewRowId, field: ownerField }}
              a1Notation={a1Notation}
              suggestionsEnabled={!rootProps.disableFormulaAutocomplete && !readOnly}
              readOnly={readOnly}
              popupId={`${id}-formula-popup`}
              ariaLabel={getLocaleText('computedColumnFormulaLabel')}
              errorSpans={errorSpans}
              onValueChange={handleFormulaChange}
              onCommitKey={handleApply}
            />
          </GridComputedColumnsPanelFormula>
          {!readOnly && (
            <GridComputedColumnsPanelReferencePane
              excludeField={draftDefinition.field}
              a1Notation={a1Notation}
              onInsert={handleInsert}
            />
          )}
        </GridComputedColumnsPanelFieldRoot>
        <GridComputedColumnsPanelFieldRoot ownerState={rootProps} className={classes.field}>
          <GridComputedColumnsPanelFieldRow ownerState={rootProps}>
            <rootProps.slots.baseSelect
              id={typeId}
              labelId={typeLabelId}
              label={getLocaleText('computedColumnTypeLabel')}
              value={draft.type}
              onChange={handleTypeChange}
              disabled={readOnly}
              size="small"
              fullWidth
              {...rootProps.slotProps?.baseSelect}
            >
              {COLUMN_TYPES.map((type) => (
                <rootProps.slots.baseSelectOption key={type} value={type} native={false}>
                  {getLocaleText(COLUMN_TYPE_LABELS[type])}
                </rootProps.slots.baseSelectOption>
              ))}
            </rootProps.slots.baseSelect>
            {draft.type === 'number' && (
              <rootProps.slots.baseSelect
                id={styleId}
                labelId={styleLabelId}
                label={getLocaleText('computedColumnFormatLabel')}
                value={draft.formatStyle}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  updateDraft({ formatStyle: event.target.value as NumberFormatStyle })
                }
                disabled={readOnly}
                size="small"
                fullWidth
                {...rootProps.slotProps?.baseSelect}
              >
                {FORMAT_STYLES.map((style) => (
                  <rootProps.slots.baseSelectOption key={style} value={style} native={false}>
                    {getLocaleText(FORMAT_STYLE_LABELS[style])}
                  </rootProps.slots.baseSelectOption>
                ))}
              </rootProps.slots.baseSelect>
            )}
          </GridComputedColumnsPanelFieldRow>
          {draft.type === 'number' && (
            <GridComputedColumnsPanelFieldRow ownerState={rootProps}>
              {draft.formatStyle === 'currency' && (
                <rootProps.slots.baseTextField
                  id={`${id}-currency`}
                  label={getLocaleText('computedColumnFormatCurrencyLabel')}
                  value={draft.currency}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateDraft({ currency: event.target.value })
                  }
                  error={currencyInvalid}
                  size="small"
                  fullWidth
                  slotProps={{
                    htmlInput: { maxLength: 3, onKeyDown: handleInputKeyDown, readOnly },
                  }}
                  {...rootProps.slotProps?.baseTextField}
                />
              )}
              <rootProps.slots.baseTextField
                id={`${id}-decimals`}
                label={getLocaleText('computedColumnFormatDecimalsLabel')}
                type="number"
                value={draft.decimals}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateDraft({ decimals: event.target.value })
                }
                error={decimalsInvalid}
                size="small"
                fullWidth
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: MAX_FRACTION_DIGITS,
                    onKeyDown: handleInputKeyDown,
                    readOnly,
                  },
                }}
                {...rootProps.slotProps?.baseTextField}
              />
            </GridComputedColumnsPanelFieldRow>
          )}
          {draft.type === 'number' && (
            <rootProps.slots.baseSwitch
              checked={draft.grouping}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateDraft({ grouping: event.target.checked })
              }
              disabled={readOnly}
              size="small"
              label={getLocaleText('computedColumnFormatGroupingLabel')}
              {...rootProps.slotProps?.baseSwitch}
            />
          )}
        </GridComputedColumnsPanelFieldRoot>
        <GridComputedColumnsPanelFieldRoot ownerState={rootProps} className={classes.field}>
          <GridComputedColumnsPanelLabel ownerState={rootProps} id={previewLabelId}>
            {getLocaleText('computedColumnPreviewLabel')}
          </GridComputedColumnsPanelLabel>
          <GridComputedColumnsPanelPreview
            ownerState={rootProps}
            className={classes.preview}
            role="status"
            aria-labelledby={previewLabelId}
          >
            <GridComputedColumnsPanelPreviewRow ownerState={rootProps}>
              {previewRowIndex === null
                ? getLocaleText('computedColumnPreviewNoRows')
                : getLocaleText('computedColumnPreviewRow')(previewRowIndex)}
            </GridComputedColumnsPanelPreviewRow>
            <GridComputedColumnsPanelPreviewValue
              ownerState={rootProps}
              data-error={preview?.error ? 'true' : undefined}
            >
              {preview?.text ?? ''}
            </GridComputedColumnsPanelPreviewValue>
          </GridComputedColumnsPanelPreview>
        </GridComputedColumnsPanelFieldRoot>
        {showValidation && (
          <GridComputedColumnsPanelValidation
            ownerState={rootProps}
            className={classes.validation}
            role="alert"
          >
            {validation.issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>{issue.message}</li>
            ))}
          </GridComputedColumnsPanelValidation>
        )}
      </GridComputedColumnsPanelEditorBody>
      <GridComputedColumnsPanelFooter ownerState={rootProps} className={classes.footer}>
        <rootProps.slots.baseButton
          ref={cancelButtonRef}
          size="small"
          onClick={handleCancel}
          {...rootProps.slotProps?.baseButton}
        >
          {getLocaleText(readOnly ? 'computedColumnCloseButton' : 'computedColumnCancelButton')}
        </rootProps.slots.baseButton>
        {isEditing && !readOnly && (
          <rootProps.slots.baseButton
            size="small"
            onClick={handleDelete}
            {...rootProps.slotProps?.baseButton}
          >
            {getLocaleText('computedColumnDeleteButton')}
          </rootProps.slots.baseButton>
        )}
        {!readOnly && (
          <rootProps.slots.baseButton
            size="small"
            onClick={handleApply}
            disabled={!canApply}
            {...rootProps.slotProps?.baseButton}
          >
            {getLocaleText(isEditing ? 'computedColumnApplyButton' : 'computedColumnAddButton')}
          </rootProps.slots.baseButton>
        )}
      </GridComputedColumnsPanelFooter>
    </GridComputedColumnsPanelEditorRoot>
  );
}

export { GridComputedColumnsPanelEditor };
